import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { ButtonBase } from '@material-ui/core';
import VehicleCheckTimeline from './VehicleCheckTimeline';
import { RouteMap } from '../map';
import tokens from '../theme/tokens';

// 확인 결과 — 추천 차량(이유) · 요약 · 예상 운임·소요시간(접수 폼에 출발·도착이 있을 때: 카카오 지도 경로 + 차량별 운임 표(행을 누르면 그 차량 기준) + 단계별 소요시간)
// · 물품 · 대안 · 포장 권고 · 주의 · 확인 필요
// · [이 내용으로 상담하기](실시간문의 초안). 모두 사이트 토큰으로만 그린다
const useStyles = makeStyles(() => ({
  root: {
    marginTop: 16,
    padding: '18px 18px 16px',
    borderRadius: 14,
    border: `1px solid ${tokens.primaryLine}`,
    backgroundColor: '#fffaf8',
  },
  head: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: '4px 10px',
  },
  vehicle: {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: -0.9,
    color: tokens.primaryDark,
  },
  reason: {
    fontSize: 13,
    letterSpacing: -0.4,
    color: tokens.text,
  },
  summary: {
    marginTop: 8,
    fontSize: 13,
    lineHeight: 1.6,
    letterSpacing: -0.4,
    color: tokens.text,
    wordBreak: 'keep-all',
  },
  sec: { marginTop: 14 },
  label: {
    display: 'block',
    marginBottom: 6,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: -0.4,
    color: tokens.muted,
  },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 28,
    padding: '4px 10px',
    borderRadius: 14,
    border: `1px solid ${tokens.line}`,
    backgroundColor: tokens.surface,
    fontSize: 12,
    letterSpacing: -0.4,
    color: tokens.text,
    lineHeight: 1.4,
    '& b': { color: tokens.ink, fontWeight: 700, marginRight: 4 },
  },
  fragile: { borderColor: tokens.primaryLine, color: tokens.primaryDark },
  line: {
    fontSize: 13,
    lineHeight: 1.6,
    letterSpacing: -0.4,
    color: tokens.text,
    wordBreak: 'keep-all',
  },
  ask: { color: tokens.primaryDark },
  alt: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginRight: 12,
    fontSize: 13,
    color: tokens.text,
    '& b': { color: tokens.ink },
  },
  travel: { marginBottom: 8, fontSize: 13, letterSpacing: -0.4, color: tokens.text, '& b': { color: tokens.ink } },
  table: { borderRadius: 10, border: `1px solid ${tokens.line}`, backgroundColor: tokens.surface, overflow: 'hidden' },
  row: {
    display: 'flex', alignItems: 'center', width: '100%', minHeight: 40, padding: '0 14px', fontSize: 13, letterSpacing: -0.4, color: tokens.text, textAlign: 'left',
    transition: 'background-color .12s',
    '& + &': { borderTop: `1px solid ${tokens.lineSoft}` },
    '&:hover': { backgroundColor: tokens.primarySoft },
  },
  map: { marginBottom: 10 },
  rowOn: { backgroundColor: '#fffaf8', '& $rv': { color: tokens.primaryDark } },
  rv: { flex: '1 1 auto', fontWeight: 700, color: tokens.ink, '& em': { marginLeft: 6, padding: '1px 7px', borderRadius: 9, backgroundColor: tokens.primary, color: tokens.surface, fontStyle: 'normal', fontSize: 11, fontWeight: 700 } },
  rf: { flex: 'none', textAlign: 'right', fontWeight: 700, color: tokens.ink, '& small': { display: 'block', fontSize: 11, fontWeight: 400, color: tokens.primaryDark } },
  consultOnly: { fontWeight: 400, color: tokens.muted },
  muted: { fontSize: 13, letterSpacing: -0.4, color: tokens.muted },
  consult: {
    display: 'inline-flex', alignItems: 'center', marginTop: 18, height: 42, padding: '0 18px', borderRadius: 21,
    backgroundColor: tokens.primary, color: tokens.surface, fontSize: 14, fontWeight: 700, letterSpacing: -0.6, transition: 'background-color .12s',
    '&:hover': { backgroundColor: tokens.primaryDark },
    '& svg': { width: 16, height: 16, marginRight: 7 },
  },
}));

const won = (n) => `${Number(n).toLocaleString('ko-KR')}원`;

function VehicleCheckResult({ result, compare, compareBusy = false, hasRoute = false, onConsult, className }) {
  const cs = useStyles();
  const rows = (compare && compare.rows) || [];
  const travel = compare && compare.travel;
  const recommended = result && result.vehicle;
  const [picked, setPicked] = useState('');            // 표에서 고른 차량 — 소요시간은 이 차량 기준(기본 추천 차량)
  const [mapFailed, setMapFailed] = useState(false);
  useEffect(() => { setPicked(''); setMapFailed(false); }, [compare]);
  if (!result) return null;
  const selected = rows.find((r) => r.vehicle === picked) || rows.find((r) => r.vehicle === recommended) || rows[0] || null;
  const { vehicle, vehicleReason, summary, items = [], alternatives = [], packaging = [], cautions = [], questions = [] } = result;
  return (
    <div className={clsx(cs.root, className)}>
      <div className={cs.head}>
        <span className={cs.vehicle}>{vehicle ? `추천 차량 · ${vehicle}` : '차량을 정하기 어려워요'}</span>
        {vehicleReason && <span className={cs.reason}>{vehicleReason}</span>}
      </div>
      {summary && <div className={cs.summary}>{summary}</div>}

      <div className={cs.sec}>
        <span className={cs.label}>예상 운임 · 소요시간</span>
        {!hasRoute && <div className={cs.muted}>출발지·도착지를 입력하면 차량별 예상 운임과 소요시간이 함께 나와요</div>}
        {hasRoute && compareBusy && <div className={cs.muted}>계산 중</div>}
        {hasRoute && !compareBusy && rows.length > 0 && (
          <React.Fragment>
            {!mapFailed && compare.path && compare.path.length > 0 && <RouteMap className={cs.map} path={compare.path} from={compare.from} to={compare.to} height={200} onError={() => setMapFailed(true)} />}
            {travel && <div className={cs.travel}>출발지 → 도착지 약 <b>{travel.km}km</b> · 차량 약 <b>{travel.min}분</b>{travel.source === 'estimate' ? ' (추정)' : ' (실제 도로)'}{compare.route === 'RoundTrip' ? ' · 왕복' : ''}{compare.isExpress === 'Y' ? ' · 급송' : ''}</div>}
            <div className={cs.table}>
              {rows.map((r) => (
                <ButtonBase key={r.vehicle} className={clsx(cs.row, selected && r.vehicle === selected.vehicle && cs.rowOn)} onClick={() => setPicked(r.vehicle)} focusRipple={false}>
                  <span className={cs.rv}>{r.vehicle}{r.vehicle === recommended && <em>추천</em>}</span>
                  <span className={clsx(cs.rf, !r.payable && cs.consultOnly)}>{r.payable && r.total ? won(r.total) : '상담 후 안내'}{r.payable && r.discount > 0 && <small>거래처 할인 −{won(r.discount)}</small>}</span>
                </ButtonBase>
              ))}
            </div>
            {selected && <VehicleCheckTimeline timeline={selected.timeline} vehicle={selected.vehicle} />}
          </React.Fragment>
        )}
        {hasRoute && !compareBusy && rows.length === 0 && <div className={cs.muted}>예상 운임을 가져오지 못했어요</div>}
      </div>

      {items.length > 0 && (
        <div className={cs.sec}>
          <span className={cs.label}>물품</span>
          <div className={cs.chips}>
            {items.map((it, i) => (
              <span key={i} className={clsx(cs.chip, it.fragile && cs.fragile)}>
                <b>{it.name}</b>{it.qty}개{it.size ? ` · ${it.size}` : ''}{it.weight ? ` · ${it.weight}` : ''}{it.fragile ? ' · 파손주의' : ''}
              </span>
            ))}
          </div>
        </div>
      )}

      {alternatives.length > 0 && (
        <div className={cs.sec}>
          <span className={cs.label}>이럴 땐</span>
          <div>{alternatives.map((a, i) => <span key={i} className={cs.alt}>{a.when} <b>{a.vehicle}</b></span>)}</div>
        </div>
      )}

      {packaging.length > 0 && (
        <div className={cs.sec}>
          <span className={cs.label}>포장 권고</span>
          <div className={cs.chips}>{packaging.map((p, i) => <span key={i} className={cs.chip}>{p}</span>)}</div>
        </div>
      )}

      {cautions.length > 0 && (
        <div className={cs.sec}>
          <span className={cs.label}>주의</span>
          <div className={cs.line}>{cautions.join(' · ')}</div>
        </div>
      )}

      {questions.length > 0 && (
        <div className={cs.sec}>
          <span className={cs.label}>확인 필요</span>
          <div className={clsx(cs.line, cs.ask)}>{questions.join(' · ')}</div>
        </div>
      )}

      {onConsult && (
        <ButtonBase className={cs.consult} onClick={() => onConsult(result, compare)} focusRipple={false}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12a8 8 0 0 1-8 8H8l-5 3 1.5-4.5A8 8 0 1 1 21 12z" /></svg>
          이 내용으로 상담하기
        </ButtonBase>
      )}
    </div>
  );
}

export default VehicleCheckResult;
